import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { formatPercentage } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store';
import { CheckError, CheckSuccess } from '@/widget/Icon';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  safeInfo: {
    top10UserPercent: number;
    burnt: boolean | number;
    blacklist: boolean;
    nomint: boolean;
    isOpenSource?: boolean;
    isHoneyPot?: boolean;
    isRenounced?: boolean;
  };
}

export default (props: IProps) => {
  const { safeInfo } = props;
  const { fwClass, chainInfo } = useAppStore();
  const { t } = useTranslation();
  const color = +safeInfo?.top10UserPercent >= 0.3 ? 'text-sellColor' : 'text-buyColor';
  const isEvm = chainInfo.chain === 'bsc';

  const count = useMemo(() => {
    let index = 0;
    if (isEvm) {
      if (+safeInfo.burnt >= 0.8) index += 1;
      if (safeInfo?.isOpenSource) index += 1;
      if (!safeInfo?.isHoneyPot) index += 1;
      if (safeInfo?.isRenounced) index += 1;
      if (safeInfo?.top10UserPercent < 0.3) index += 1;
      return index;
    } else {
      if (safeInfo?.burnt) index += 1;
      if (!safeInfo?.blacklist) index += 1;
      if (safeInfo?.nomint) index += 1;
      if (safeInfo?.top10UserPercent < 0.3) index += 1;
      return index;
    }
  }, [safeInfo, isEvm]);

  const [open, setOpen] = useState(false);

  const totalChecks = isEvm ? 5 : 4;
  const safeColor = count === totalChecks ? 'bg-[#0ECB8126] text-buyColor' : 'bg-[#F6465D26] text-sellColor';
  const img = count === totalChecks ? '/icon/safe.png' : '/icon/risk.svg';
  const renderCount = count === totalChecks ? totalChecks : totalChecks - count;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        <div className="flex items-center">
          <img src={img} className="size-[18px] mr-[4px]" />
          <span className={cn('text-[12px] rounded-[4px] px-[4px]', safeColor)}>
            {renderCount}/{totalChecks}
          </span>
        </div>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="p-[16px] border-t-[1px] border-t-borderColor rounded-tl-[12px] rounded-tr-[12px] outline-none"
      >
        <SheetHeader className="hidden" />
        <SheetTitle className="hidden" />
        <SheetDescription className="hidden" />
        <div className="flex flex-col gap-y-[16px]">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-[4px]">
              <img src={img} className="size-[18px]" />
              <span className={cn(fwClass, 'text-[24px]')}>{count === totalChecks ? t('Signal.safe') : t('Signal.risk')}</span>
              <span className={cn(' text-[12px] rounded-[4px] px-[4px]', safeColor)}>
                {renderCount}/{totalChecks}
              </span>
            </div>
            <X className="size-[16px]" onClick={() => setOpen(false)} />
          </div>

          {isEvm ? (
            // EVM 链的安全检查项
            <>
              <div className="flex justify-between items-center">
                <span>{t('Signal.openSource')}</span>
                {safeInfo?.isOpenSource ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.honeypot')}</span>
                {!safeInfo?.isHoneyPot ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.burnt')}</span>
                {typeof safeInfo.burnt == 'string' || safeInfo.burnt == 0 ? (
                  <span className={+safeInfo.burnt >= 0.8 ? 'text-buyColor' : 'text-sellColor'}>
                    {formatPercentage(safeInfo.burnt, { scale: true, showPositiveSign: false })}
                  </span>
                ) : safeInfo?.burnt ? (
                  <CheckSuccess className="size-[16px]" />
                ) : (
                  <CheckError className="size-[16px]" />
                )}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.renounced')}</span>
                {safeInfo?.isRenounced ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.top10')}</span>
                <span className={color}>{formatPercentage(safeInfo?.top10UserPercent, { scale: true, showPositiveSign: false })}</span>
              </div>
            </>
          ) : (
            // SOL 链的原有安全检查项
            <>
              <div className="flex justify-between items-center">
                <span>{t('Signal.noMint')}</span>
                {safeInfo?.nomint ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.blackList')}</span>
                {!safeInfo?.blacklist ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.burnt')}</span>
                {safeInfo?.burnt ? <CheckSuccess className="size-[16px]" /> : <CheckError className="size-[16px]" />}
              </div>
              <div className="flex justify-between items-center">
                <span>{t('Signal.top10')}</span>
                <span className={color}>{formatPercentage(safeInfo?.top10UserPercent, { scale: true, showPositiveSign: false })}</span>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
